import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AdSlot from "@/components/AdSlot";
import { useAdSetting } from "@/lib/adSettings";

const TYPE_COLOR = { team: "#ff4500", trainer: "#2196f3", event: "#22c55e" };
const LINK = { team: "teams", trainer: "trainer-listings", event: "events" };
const SECONDS_PER_ITEM = 3; // each card takes ~3s to travel across

// Continuous right→left slideshow of team/trainer logos & event flyers, each
// labelled with its city + 2-letter state in the listing's color. Pure-CSS
// marquee (no JS timers); pauses on hover; each card links to its detail page.
export default function HeroSlideshow({ sport }) {
    const [items, setItems] = useState([]);
    const adsOn = useAdSetting("slideshow");

    useEffect(() => {
        let alive = true;
        api.get("/stats/showcase", { params: { sport } })
            .then((r) => { if (alive) setItems(r.data?.items || []); })
            .catch(() => { if (alive) setItems([]); });
        return () => { alive = false; };
    }, [sport]);

    if (!items.length) {
        return <div className="w-full h-full bg-[#111]/60 border border-[#2a2a2a]" data-testid="hero-slideshow-empty" />;
    }

    // Placement 3: insert an ad card as every 6th slide (after items 6, 12, ...).
    const withAds = [];
    items.forEach((it, i) => {
        withAds.push({ __ad: false, it });
        if (adsOn && (i + 1) % 6 === 0) withAds.push({ __ad: true, key: `ad-${i}` });
    });

    const loop = [...withAds, ...withAds];
    const duration = withAds.length * SECONDS_PER_ITEM;

    return (
        <div className="hero-marquee relative w-full h-full overflow-hidden border border-[#2a2a2a] bg-[#0d0d0d]" data-testid="hero-slideshow">
            {/* soft edge fades so cards enter/exit gracefully */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-8 z-10 bg-gradient-to-r from-[#0a0a0a] to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-8 z-10 bg-gradient-to-l from-[#0a0a0a] to-transparent" />
            <div className="hero-marquee-track h-full" style={{ animationDuration: `${duration}s` }}>
                {loop.map((entry, i) =>
                    entry.__ad
                        ? <AdCard key={`${entry.key}-${i}`} />
                        : <Card key={`${entry.it.type}-${entry.it.id}-${i}`} item={entry.it} />
                )}
            </div>
        </div>
    );
}

// An ad slide styled to match the square slideshow cards.
function AdCard() {
    const slot = process.env.REACT_APP_ADSENSE_SLIDESHOW_SLOT;
    return (
        <div className="relative h-full aspect-square shrink-0 bg-[#0f0f0f]" data-testid="hero-slide-ad">
            <div className="absolute top-1 left-1 z-10 text-[8px] uppercase tracking-[0.2em] text-[#8a8a8a] bg-black/70 px-1 py-0.5 rounded-sm">Sponsored</div>
            <AdSlot slot={slot} format="rectangle" responsive={false} minHeight={0} className="h-full w-full" style={{ height: "100%" }} placement="slideshow" />
        </div>
    );
}

function Card({ item }) {
    const color = TYPE_COLOR[item.type] || "#ff4500";
    const isEvent = item.type === "event";
    // Events are full-bleed flyers (cropped), unless the backend flags them as a logo (contain).
    const contain = !isEvent || item.fit === "contain";
    const bg = item.preview_bg === "black" ? "bg-black" : item.preview_bg === "clear" ? "bg-transparent" : isEvent ? "bg-black" : "bg-white";
    return (
        <Link
            to={`/${LINK[item.type]}/${item.id}`}
            data-testid={`hero-slide-${item.type}-${item.id}`}
            className="relative h-full aspect-square shrink-0 group"
            title={`${item.city}, ${item.state}`}
        >
            <img
                src={item.image}
                alt={`${item.city}, ${item.state}`}
                loading="lazy"
                onError={(e) => { const a = e.currentTarget.closest("a"); if (a) a.style.display = "none"; }}
                className={`w-full h-full ${contain ? "object-contain p-2" : "object-cover"} ${bg}`}
            />
            {item.city && item.state && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent px-1.5 pt-4 pb-1">
                    <div
                        className="text-[10px] sm:text-xs font-display tracking-wide leading-tight truncate text-center"
                        style={{ color }}
                        data-testid="hero-slide-location"
                    >
                        {item.city}, {item.state}
                    </div>
                </div>
            )}
        </Link>
    );
}
